import { type Dispatch, type SetStateAction } from 'react'
import { Check, KeyRound, ShieldCheck, X } from 'lucide-react'
import { type UserRole } from '~/api/users'
import { Badge } from '~/components/ui/badge'
import { BulkActionBar } from '~/components/ui/bulk-action-bar'
import { Button } from '~/components/ui/button'
import { Checkbox } from '~/components/ui/checkbox'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { Select } from '~/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '~/components/ui/table'
import {
    type SelectionState,
    type TableSelection,
} from '~/lib/use-table-selection'
import {
    formatDate,
    ROLE_TONE,
    STATUS_LABEL,
    STATUS_TONE,
    type PendingRole,
    type PendingStatus,
    type UserRow,
    type UserStatus,
} from './constants'

// 검색·가입상태 필터 + 일괄작업 바 + 사용자 표(역할/가입상태 변경·비밀번호 재설정 트리거).
export function UsersTable({
    query,
    setQuery,
    statusFilter,
    setStatusFilter,
    selection,
    bulkRole,
    setBulkRole,
    bulkBusy,
    setBulkConfirm,
    filtered,
    visibleIds,
    headState,
    isSuperadmin,
    setStatusPending,
    setPending,
    setPwdTarget,
}: {
    query: string
    setQuery: Dispatch<SetStateAction<string>>
    statusFilter: 'all' | UserStatus
    setStatusFilter: Dispatch<SetStateAction<'all' | UserStatus>>
    selection: TableSelection<string>
    bulkRole: UserRole
    setBulkRole: Dispatch<SetStateAction<UserRole>>
    bulkBusy: boolean
    setBulkConfirm: Dispatch<SetStateAction<boolean>>
    filtered: UserRow[]
    visibleIds: string[]
    headState: SelectionState
    isSuperadmin: boolean
    setStatusPending: Dispatch<SetStateAction<PendingStatus | null>>
    setPending: Dispatch<SetStateAction<PendingRole | null>>
    setPwdTarget: Dispatch<SetStateAction<UserRow | null>>
}) {
    return (
        <>
            {/* 검색 + 가입 상태 필터 */}
            <div className='mb-3 flex flex-wrap items-end gap-3'>
                <div className='flex flex-col gap-1'>
                    <Label htmlFor='user-search'>검색</Label>
                    <Input
                        id='user-search'
                        className='h-9 w-72'
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder='이메일 또는 이름'
                    />
                </div>
                <div className='flex flex-col gap-1'>
                    <Label htmlFor='status-filter'>가입 상태</Label>
                    <Select
                        id='status-filter'
                        value={statusFilter}
                        onChange={(e) =>
                            setStatusFilter(
                                e.target.value as 'all' | UserStatus
                            )
                        }
                        className='h-9 w-40'
                        aria-label='가입 상태 필터'>
                        <option value='all'>전체</option>
                        <option value='pending'>승인 대기</option>
                        <option value='active'>활성</option>
                        <option value='rejected'>거부</option>
                    </Select>
                </div>
            </div>

            {/* 선택 시 떠오르는 일괄작업 바 — 역할을 고른 뒤 확인을 거쳐 일괄 변경한다. */}
            <div className='mb-3'>
                <BulkActionBar
                    count={selection.count}
                    onClear={selection.clear}
                    sticky
                    position='top'>
                    <Label htmlFor='bulk-role'>역할</Label>
                    <Select
                        id='bulk-role'
                        value={bulkRole}
                        onChange={(e) =>
                            setBulkRole(e.target.value as UserRole)
                        }
                        className='h-8 w-auto'
                        aria-label='일괄 변경할 역할'>
                        <option value='user'>user</option>
                        <option value='admin'>admin</option>
                        <option value='superadmin'>superadmin</option>
                    </Select>
                    <Button
                        variant='outline'
                        size='sm'
                        disabled={bulkBusy}
                        onClick={() => setBulkConfirm(true)}>
                        <ShieldCheck />
                        {bulkBusy ? '변경하는 중…' : '역할 일괄 변경'}
                    </Button>
                </BulkActionBar>
            </div>

            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className='w-9'>
                            <Checkbox
                                aria-label='현재 목록 전체 선택'
                                checked={headState === 'all'}
                                indeterminate={headState === 'some'}
                                disabled={visibleIds.length === 0}
                                onChange={() => selection.toggleAll(visibleIds)}
                            />
                        </TableHead>
                        <TableHead>이메일</TableHead>
                        <TableHead>이름</TableHead>
                        <TableHead>역할</TableHead>
                        <TableHead>조직</TableHead>
                        <TableHead>가입일</TableHead>
                        <TableHead>상태</TableHead>
                        <TableHead>작업</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {filtered.length === 0 ? (
                        <TableRow>
                            <TableCell
                                colSpan={8}
                                className='text-muted-foreground py-8 text-center'>
                                {query.trim()
                                    ? '검색 결과가 없어요.'
                                    : '사용자가 없어요.'}
                            </TableCell>
                        </TableRow>
                    ) : (
                        filtered.map((u) => (
                            <TableRow key={u.id}>
                                {/* 체크박스 클릭은 행 동작과 분리한다(stopPropagation). */}
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                    <Checkbox
                                        aria-label={`${u.email} 선택`}
                                        checked={selection.isSelected(u.id)}
                                        onChange={() => selection.toggle(u.id)}
                                    />
                                </TableCell>
                                <TableCell>{u.email}</TableCell>
                                <TableCell>{u.name}</TableCell>
                                <TableCell>
                                    <Badge
                                        tone={ROLE_TONE[u.role] ?? 'neutral'}>
                                        {u.role}
                                    </Badge>
                                </TableCell>
                                <TableCell>{u.organization ?? '-'}</TableCell>
                                <TableCell>{formatDate(u.createdAt)}</TableCell>
                                <TableCell>
                                    <Badge
                                        tone={
                                            STATUS_TONE[u.status ?? 'active']
                                        }>
                                        {STATUS_LABEL[u.status ?? 'active']}
                                    </Badge>
                                </TableCell>
                                {/* 값은 현재 역할로 고정한다. 변경은 확인 후에만 반영하며,
                                    취소 시 자연스럽게 원래 값으로 되돌아간다. */}
                                <TableCell>
                                    <div className='flex items-center gap-2'>
                                        {u.status === 'pending' ? (
                                            <>
                                                <Button
                                                    variant='success'
                                                    size='xs'
                                                    onClick={() =>
                                                        setStatusPending({
                                                            user: u,
                                                            status: 'active',
                                                        })
                                                    }>
                                                    <Check />
                                                    승인
                                                </Button>
                                                <Button
                                                    variant='outline'
                                                    size='xs'
                                                    onClick={() =>
                                                        setStatusPending({
                                                            user: u,
                                                            status: 'rejected',
                                                        })
                                                    }>
                                                    <X />
                                                    거부
                                                </Button>
                                            </>
                                        ) : null}
                                        <Select
                                            value={u.role}
                                            onChange={(e) =>
                                                setPending({
                                                    user: u,
                                                    role: e.target.value,
                                                })
                                            }
                                            className='h-8 w-auto'
                                            aria-label={`${u.email} 역할 변경`}>
                                            <option value='user'>user</option>
                                            <option value='admin'>admin</option>
                                            <option value='superadmin'>
                                                superadmin
                                            </option>
                                        </Select>
                                        {/* 비밀번호 재설정은 superadmin 전용(이메일 복구가 없어 콘솔에서 처리). */}
                                        {isSuperadmin ? (
                                            <Button
                                                variant='outline'
                                                size='xs'
                                                onClick={() => setPwdTarget(u)}
                                                aria-label={`${u.email} 비밀번호 재설정`}>
                                                <KeyRound />
                                                비밀번호 재설정
                                            </Button>
                                        ) : null}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </>
    )
}
